// v0.6.10 — bulk resolution of one calc's dropped-transaction backlog.
//
// WHY. Resolution was per-entry only, which is fine for the handful of
// rows an operator drops deliberately. But one mis-scoped drop can
// create hundreds at once: on 2026-09-23 a tolerance-band defect in the
// at-rate selection put 830 correct transactions into this backlog
// across two runs. Clearing that by hand is 830 clicks, and an operator
// facing 830 clicks leaves the backlog permanently dirty instead —
// which defeats the gate, because real entries then hide in the noise.
//
// Deliberately scoped to ONE calc and gated on a typed confirmation of
// the exact count, because the failure that created those entries was
// precisely a bulk action whose real scope the operator could not see.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { API } from "../lib/api";
import { getToken } from "../lib/authApi";
import { writeFetch } from "../lib/sharedRead";   // A1-95
import { BACKLOG_REASONS } from "../lib/reviewReasonPresets";
import { FiledCalcSelect, laterFilingFilter } from "./FiledCalcSelect";

export const BulkResolveByCalc = ({ calcId, openCount, onDone }) => {
  // Sweep A5-27 (2026-10-11): t() was called below but never defined, so
  // opening "Resolve all" threw "t is not defined" (A1-68's class).
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [resolution, setResolution] = useState("filed_later");
  const [filedInCalc, setFiledInCalc] = useState("");
  const [note, setNote] = useState("");
  const [preset, setPreset] = useState("");
  const [confirmCount, setConfirmCount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const countMatches = Number(confirmCount) === Number(openCount);
  const valid = note.trim().length >= 10
    && countMatches
    && !/[<>]/.test(note)
    && (resolution !== "filed_later" || filedInCalc.trim().length > 0);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await writeFetch(
        `${API}/admin/excluded-txns/by-calc/${calcId}/resolve-all`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            resolution,
            resolution_note: note.trim(),
            filed_in_calc_id:
              resolution === "filed_later" ? filedInCalc.trim() : null,
            expected_count: Number(openCount),
          }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = body?.detail;
        throw new Error(
          d?.message || (typeof d === "string" ? d : null)
          || `HTTP ${res.status}`,
        );
      }
      setShow(false);
      if (onDone) onDone(body);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!show) {
    // The label carries a loaded resolution word ("Resolve") but this
    // button RESOLVES NOTHING — it opens the panel below, which is
    // where the operator picks a resolution and confirms the count.
    // Tagging it `data-resolution-class` would be a lie (it calls no
    // endpoint); `aria-describedby` is the UX audit's own sanctioned
    // alternative and is the honest one, since it points at the text
    // saying what the button actually does.
    return (
      <>
        <button
          type="button"
          data-testid={`xtxn-bulk-open-${calcId}`}
          aria-describedby={`xtxn-bulk-open-desc-${calcId}`}
          onClick={() => setShow(true)}
          className="text-[11px] underline text-slate-600 dark:text-slate-300"
        >
          {t("ui.excluded_backlog.bulk_open", { n: openCount, defaultValue: `Resolve all ${openCount} from this calculation →` })}
        </button>
        <span
          id={`xtxn-bulk-open-desc-${calcId}`}
          className="sr-only"
        >
          {t("ui.excluded_backlog.bulk_open_desc", { n: openCount, defaultValue: `Opens a form to record one resolution for all ${openCount} entries dropped from this calculation. Nothing is resolved until you confirm the count there.` })}
        </span>
      </>
    );
  }

  return (
    <div
      data-testid={`xtxn-bulk-form-${calcId}`}
      className="mt-2 p-3 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
    >
      <div className="text-xs font-bold text-slate-900 dark:text-slate-100">
        {t("ui.excluded_backlog.bulk_form_title", { n: openCount, defaultValue: `Resolve all ${openCount} entries from calculation` })}{" "}
        <span className="tabular-nums">{String(calcId).slice(0, 8)}…</span>
      </div>
      <div className="text-[11px] text-slate-600 dark:text-slate-300 mt-1">
        {t("ui.excluded_backlog.bulk_nothing_deleted", { defaultValue: "Nothing is deleted. Each entry keeps its full history and stays visible under the resolved filters." })}
      </div>
      <div className="mt-2 flex flex-col gap-1 text-xs text-slate-700 dark:text-slate-200">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            data-testid={`xtxn-bulk-filed-${calcId}`}
            checked={resolution === "filed_later"}
            onChange={() => { setResolution("filed_later"); setPreset(""); setNote(""); }}
          />
          {t("ui.excluded_backlog.radio_filed_later", { defaultValue: "Filed in a later calculation" })}
        </label>
        {resolution === "filed_later" && (
          // v0.6.12 A5-27 — picked, not pasted. The backend refuses this
          // same calculation ("must be a DIFFERENT calc"), so it is not
          // offered.
          <FiledCalcSelect
            testid={`xtxn-bulk-calcid-${calcId}`}
            value={filedInCalc}
            onChange={(id) => setFiledInCalc(id)}
            filter={laterFilingFilter(calcId)}
            aria-label={t("ui.excluded_backlog.filed_calc_pick", {
              defaultValue: "The calculation where this transaction was reported",
            })}
          />
        )}
        <label className="flex items-center gap-2">
          <input
            type="radio"
            data-testid={`xtxn-bulk-notsale-${calcId}`}
            checked={resolution === "not_a_sale"}
            onChange={() => { setResolution("not_a_sale"); setPreset(""); setNote(""); }}
          />
          {t("ui.excluded_backlog.bulk_not_a_sale", { defaultValue: "Not a real sale (bad data)" })}
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input
            type="radio" name="bulk-res"
            data-testid="bulk-radio-another-client"
            checked={resolution === "another_client"}
            onChange={() => { setResolution("another_client"); setPreset(""); setNote(""); }}
          />
          {t("ui.excluded_backlog.radio_another_client")}
        </label>
      </div>
      {/* v0.6.10 — pick, don't write. The two resolutions get
          different lists: offering "not a real sale" reasons under
          "filed in a later calc" would invite the wrong record. */}
      <select
        data-testid={`xtxn-bulk-preset-${calcId}`}
        value={preset}
        onChange={(e) => {
          setPreset(e.target.value);
          setNote(e.target.value);
        }}
        className="w-full mt-2 text-[11px] px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
      >
        <option value="">{t("ui.review_grouped.reason_pick", { defaultValue: "Choose a reason…" })}</option>
        {(BACKLOG_REASONS[resolution] || []).map(
          (r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <label className="block mt-2 text-[11px] text-slate-600 dark:text-slate-300">
        {t("ui.excluded_backlog.bulk_type", { defaultValue: "Type" })} <strong>{openCount}</strong> {t("ui.excluded_backlog.bulk_type_after", { defaultValue: "to confirm the number you are resolving:" })}
        <input
          data-testid={`xtxn-bulk-confirm-${calcId}`}
          value={confirmCount}
          onChange={(e) => setConfirmCount(e.target.value)}
          className="ml-2 w-20 tabular-nums text-[11px] px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
        />
      </label>
      {err && (
        <div
          data-testid={`xtxn-bulk-error-${calcId}`}
          className="mt-2 text-[11px] text-rose-700 dark:text-rose-300"
        >
          {err}
        </div>
      )}
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          data-testid={`xtxn-bulk-cancel-${calcId}`}
          onClick={() => setShow(false)}
          className="text-[11px] px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200"
        >
          {t("ui.excluded_backlog.bulk_cancel", { defaultValue: "Cancel" })}
        </button>
        <button
          type="button"
          data-testid={`xtxn-bulk-submit-${calcId}`}
          data-resolution-class="recordkeeping"
          data-resolution-endpoint="POST /api/admin/excluded-txns/by-calc/{calc_id}/resolve-all"
          disabled={busy || !valid}
          onClick={submit}
          className="text-[11px] font-bold px-3 py-1.5 rounded bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? t("ui.excluded_backlog.bulk_resolving", { defaultValue: "Resolving…" }) : t("ui.excluded_backlog.bulk_submit", { n: openCount, defaultValue: `Resolve ${openCount}` })}
        </button>
      </div>
    </div>
  );
};

export default BulkResolveByCalc;
